import { type FC } from "react";
import { generatePath } from "react-router-dom";

import { Group, Paper, Stack, Text } from "@mantine/core";
import { useInputState } from "@mantine/hooks";
import { Spacing } from "@snapchat/snap-design-system";
import { FolderIcon, Sizes } from "@snapchat/snap-design-system-icons";

import { AbsoluteRoute } from "../../../core/routes";
import { MyLensesRoleType, ResourceKind } from "../../../gql-generated/types";
import { PageContentSpinner } from "../../../layout/components";
import { Flexbox } from "../../../shared/components";
import { LensAutomationToolAlert } from "../../../shared/components/LensAutomationToolAlert";
import { useOrgChildResources } from "../../../shared/hooks";
import { LinkStyled } from "../../../shared/styles";
import { NoLensFoldersCard } from "../no-lens-folders/NoLensFoldersCard";

import { folderIconContainer, folderSubTitle, folderTitle, paper } from "./LensFoldersList.module.css";
import { LensFoldersPageHeader } from "./header";

const makeRoleType = (rolesList: readonly MyLensesRoleType[] = []): string => {
    if (rolesList.includes(MyLensesRoleType.RoleTypeLensFolderAdmin)) {
        return "Admin";
    } else if (rolesList.includes(MyLensesRoleType.RoleTypeLensFolderManager)) {
        return "Manager";
    } else if (rolesList.includes(MyLensesRoleType.RoleTypeLensFolderCreator)) {
        return "Creator";
    }
    return "";
};

export const LensFoldersListV2: FC = () => {
    const {
        resourceContainers,
        loading,
        error: errorFetchingLensFolders,
    } = useOrgChildResources({ shouldSkip: false, kind: ResourceKind.LensSpaces });

    const lensFoldersList = resourceContainers;
    const [searchText, setSearchText] = useInputState("");

    const lensFoldersListFiltered = lensFoldersList.filter((lensFolder) => {
        // TODO - https://en.wikipedia.org/wiki/Levenshtein_distance
        const searchTextLower = searchText.toLocaleLowerCase();
        const foldersListFiltered = lensFolder.name.toLocaleLowerCase().includes(searchTextLower);
        return foldersListFiltered;
    });

    if (loading) {
        return <PageContentSpinner />;
    }

    if (errorFetchingLensFolders) {
        return <NoLensFoldersCard />;
    }

    return (
        <Stack data-testid="lens-folders-grid">
            <Flexbox flexDirection={"column"}>
                <LensAutomationToolAlert />
                <LensFoldersPageHeader searchText={searchText} setSearchText={setSearchText} />
            </Flexbox>
            {lensFoldersListFiltered.length > 0 ? (
                <Stack mb={20} gap={0}>
                    {lensFoldersListFiltered.map((lensFolder) => {
                        const route = generatePath(AbsoluteRoute.ORG_LENS_FOLDER_LENSES, {
                            orgId: lensFolder.parentName,
                            lensFolderId: lensFolder.id,
                        });

                        return (
                            <LinkStyled to={route} key={lensFolder.id}>
                                <Paper p={Spacing.XS} radius={0} data-testid="lens-folders-grid-card" className={paper}>
                                    <Group>
                                        <div className={folderIconContainer}>
                                            <FolderIcon size={Sizes.Large} />
                                        </div>
                                        <Stack gap="xs">
                                            <Text component="span" className={`paragraph-p200 ${folderTitle}`}>
                                                {lensFolder.name}
                                            </Text>
                                            <Text
                                                component="span"
                                                className={`paragraph-p300 ${folderSubTitle}`}
                                                c="dimmed"
                                            >
                                                {makeRoleType(lensFolder.roles)}
                                            </Text>
                                        </Stack>
                                    </Group>
                                </Paper>
                            </LinkStyled>
                        );
                    })}
                </Stack>
            ) : (
                <NoLensFoldersCard fromSearch={lensFoldersList.length > 0} />
            )}
        </Stack>
    );
};
